import { useNavigate } from 'react-router-dom'
import { Printer, RefreshCw } from 'lucide-react'
import { useTasks } from '@/hooks/useTasks'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { WorkerPrintPalletModal } from '@/components/features/worker/WorkerPrintPalletModal'
import { TASK_STATUS } from '@/lib/constants'
import { workerRoutes } from '@/lib/navigation/routes'
import { formatDateTime } from '@/lib/utils'
import { useState } from 'react'
import type { TaskCreationMode, TaskSummary } from '@/types'

const CREATION_MODE_LABEL: Record<TaskCreationMode, string> = {
  km_and_ka_file: 'КМ + КА из файлов',
  ka_file_only: 'Только КА из файлов',
  km_file_only: 'Только КМ из файла',
}

function modeBadge(task: TaskSummary) {
  if (task.creation_mode === 'km_and_ka_file') return null
  const label = task.creation_mode ? CREATION_MODE_LABEL[task.creation_mode] : 'Другой режим'
  return (
    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
      {label ?? 'скоро'}
    </span>
  )
}

export function WorkerTaskListPage() {
  const navigate = useNavigate()
  const { data: tasks, isLoading, refetch, isFetching } = useTasks()
  const [printPalletOpen, setPrintPalletOpen] = useState(false)

  const visibleTasks = (tasks ?? []).filter(t => t.status === 'in_progress')

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Задания</h1>
          <p className="text-sm text-gray-500 mt-1">Задания в статусе «В работе»</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => setPrintPalletOpen(true)}>
            <Printer className="w-4 h-4 mr-1" />
            Печать паллет
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} isLoading={isFetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!visibleTasks.length ? (
        <div className="text-center text-gray-500 py-20">
          <p className="text-lg">Нет заданий в работе</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Название</th>
                <th className="px-4 py-3">Комментарий</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Коробки</th>
                <th className="px-4 py-3">Паллеты</th>
                <th className="px-4 py-3">Создано</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visibleTasks.map(t => {
                const meta = TASK_STATUS[t.status]
                return (
                  <tr
                    key={t.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(workerRoutes.taskDetail(t.id))}
                  >
                    <td className="px-4 py-3 font-mono text-xs">{t.id}</td>
                    <td className="px-4 py-3 font-medium">
                      {t.name || `Задание ${t.id}`}
                      {modeBadge(t)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={t.comment || undefined}>
                      {t.comment?.trim() || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge color={meta.color} textColor={meta.textColor}>{meta.label}</StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-center">{t.boxes_count}</td>
                    <td className="px-4 py-3 text-center">{t.pallets_count}</td>
                    <td className="px-4 py-3 text-gray-500">{formatDateTime(t.created_at)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {printPalletOpen && (
        <WorkerPrintPalletModal onClose={() => setPrintPalletOpen(false)} />
      )}
    </div>
  )
}
